import { __assign, __awaiter, __generator, __read, __rest } from "tslib";
import React, { useState, useImperativeHandle, forwardRef, useRef, useEffect, useMemo, useContext, memo, } from "react";
import classNames from "classnames";
import { TreeContext } from "./TreeContext";
import { Check } from "../check";
import { Icon } from "../icon";
import { injectValue } from "../_util/inject-value";
import { useConfig } from "../_util/config-context";
import { forwardRefWithStatics } from "../_util/forward-ref-with-statics";
import { mergeEventProps } from "../_util/merge-event-props";
import { TreeNode as DeprecatedTreeNode } from "./deprecated/TreeNode";
export var TreeActionLink = React.forwardRef(function TreeActionLink(_a, ref) {
    var children = _a.children, className = _a.className, props = __rest(_a, ["children", "className"]);
    var classPrefix = useConfig().classPrefix;
    return (React.createElement("a", __assign({ ref: ref, className: classNames(classPrefix + "-text--link", className) }, props), children));
});
TreeActionLink.displayName = "TreeActionLink";
export var TreeNode = forwardRefWithStatics(function TreeNode(props, ref) {
    var _a = useContext(TreeContext), flatten = _a.flatten, activable = _a.activable, onActive = _a.onActive, expandedIds = _a.expandedIds, fullExpandable = _a.fullExpandable, fullActivable = _a.fullActivable, onExpand = _a.onExpand, onLoad = _a.onLoad, onLoadError = _a.onLoadError, switcherIcon = _a.switcherIcon, treeSelectable = _a.selectable;
    var expanded = useMemo(function () { return expandedIds.includes(props.id); }, [
        expandedIds,
        props.id,
    ]);
    if (!flatten) {
        return React.createElement(DeprecatedTreeNode, __assign({}, props, { ref: ref }));
    }
    return (React.createElement(TreeNodeInner, __assign({}, props, { ref: ref, activable: activable, onActive: onActive, fullExpandable: fullExpandable, fullActivable: fullActivable, expanded: expanded, onExpand: onExpand, onLoad: onLoad, onLoadError: onLoadError, switcherIcon: switcherIcon, treeSelectable: treeSelectable })));
}, {
    ActionLink: TreeActionLink,
});
TreeNode.displayName = "TreeNode";
var TreeNodeInner = memo(forwardRef(function TreeNodeInner(allProps, ref) {
    var id = allProps.id, _a = allProps.level, level = _a === void 0 ? 0 : _a, content = allProps.content, icon = allProps.icon, operation = allProps.operation, expandable = allProps.expandable, children = allProps.children, nodeSelectable = allProps.selectable, 
    // Context
    activable = allProps.activable, fullActivable = allProps.fullActivable, onActive = allProps.onActive, fullExpandable = allProps.fullExpandable, expanded = allProps.expanded, onExpand = allProps.onExpand, onLoad = allProps.onLoad, onLoadError = allProps.onLoadError, switcherIcon = allProps.switcherIcon, treeSelectable = allProps.treeSelectable, 
    // 外层使用
    disableSelect = allProps.disableSelect, pure = allProps.pure, className = allProps.className, style = allProps.style, 
    // 节点内无用
    height = allProps.height, pathKey = allProps.pathKey, 
    // 可透传属性
    props = __rest(allProps, ["id", "level", "content", "icon", "operation", "expandable", "children", "selectable", "activable", "fullActivable", "onActive", "fullExpandable", "expanded", "onExpand", "onLoad", "onLoadError", "switcherIcon", "treeSelectable", "disableSelect", "pure", "className", "style", "height", "pathKey"]);
    var classPrefix = useConfig().classPrefix;
    var selectable = typeof nodeSelectable !== "undefined"
        ? nodeSelectable && treeSelectable
        : treeSelectable;
    var nodeIcon = injectValue(icon)({ expanded: expanded });
    var switcherRef = useRef(null);
    function handleExpand(event) {
        if (switcherRef.current) {
            switcherRef.current.handleExpand(event);
        }
    }
    // activable 与 selectable 同时开启时 Checkbox 与内容结构分离
    var nodeContent = activable && selectable ? (React.createElement("div", { className: classPrefix + "-tree__label" },
        React.createElement(Check, { name: id, type: "checkbox", content: content }),
        React.createElement("span", { className: classPrefix + "-tree__label-title", onClick: function (event) {
                if (!fullActivable) {
                    onActive(id, event, allProps);
                }
            } },
            nodeIcon,
            content,
            operation && (React.createElement("div", { className: classPrefix + "-tree__action", onClick: function (e) { return e.stopPropagation(); } }, operation))))) : (React.createElement("div", { className: classPrefix + "-tree__label", onClick: function (event) {
            if (!fullActivable && activable) {
                onActive(id, event, allProps);
            }
        } },
        selectable ? (React.createElement(Check, { name: id, content: content, type: "checkbox" },
            nodeIcon,
            content)) : (React.createElement(React.Fragment, null,
            nodeIcon,
            React.createElement("span", { className: classPrefix + "-tree__label-title" }, content))),
        operation && (React.createElement("div", { className: classPrefix + "-tree__action", onClick: function (e) { return e.stopPropagation(); } }, operation))));
    return (React.createElement("div", __assign({ ref: ref, className: classPrefix + "-tree__node-content", style: fullActivable || fullExpandable ? { cursor: "pointer" } : undefined, 
        // eslint-disable-next-line dot-notation
        draggable: props["draggable"] }, mergeEventProps(props, {
        onClick: function (event) {
            if (fullActivable && activable) {
                onActive(id, event, allProps);
            }
            if (fullExpandable && Boolean(expandable || children)) {
                handleExpand(event);
            }
        },
    })),
        new Array(level).fill(null).map(function (_, i) { return (React.createElement("span", { key: i, className: classPrefix + "-tree__space" })); }),
        React.createElement(TreeNodeSwitcher, { id: id, ref: switcherRef, icon: switcherIcon, enable: Boolean(expandable || children), expanded: expanded, onExpand: onExpand, onLoad: onLoad, onLoadError: onLoadError, classPrefix: classPrefix, allProps: allProps }),
        nodeContent));
}));
TreeNodeInner.displayName = "TreeNodeInner";
var TreeNodeSwitcher = forwardRef(function TreeNodeSwitcher(_a, ref) {
    var id = _a.id, enable = _a.enable, expanded = _a.expanded, onExpand = _a.onExpand, onLoad = _a.onLoad, _b = _a.onLoadError, onLoadError = _b === void 0 ? function () { return null; } : _b, _c = _a.icon, icon = _c === void 0 ? function (_a) {
        var expanded = _a.expanded;
        return (React.createElement(Icon, { type: expanded ? "arrowdown" : "arrowright" }));
    } : _c, classPrefix = _a.classPrefix, allProps = _a.allProps;
    var _d = __read(useState(false), 2), loading = _d[0], setLoading = _d[1];
    var _e = __read(useState(false), 2), error = _e[0], setError = _e[1];
    var onExpandRef = useRef(onExpand);
    useEffect(function () {
        onExpandRef.current = onExpand;
    }, [onExpand]);
    var switcherIcon = injectValue(icon)({ expanded: expanded, nodeId: id });
    useImperativeHandle(ref, function () { return ({ handleExpand: handleExpand }); });
    function handleExpand(event) {
        return __awaiter(this, void 0, void 0, function () {
            var err_1;
            return __generator(this, function (_a) {
                switch (_a.label) {
                    case 0:
                        event.stopPropagation();
                        if (expanded) {
                            onExpand(id, false, event, allProps);
                            return [2 /*return*/];
                        }
                        if (!onLoad) return [3 /*break*/, 6];
                        setError(false);
                        setLoading(true);
                        _a.label = 1;
                    case 1:
                        _a.trys.push([1, 3, 4, 5]);
                        return [4 /*yield*/, onLoad(id)];
                    case 2:
                        _a.sent();
                        // 外层 expand 状态变化导致 onExpand 更新
                        onExpandRef.current(id, true, event, allProps);
                        return [3 /*break*/, 5];
                    case 3:
                        err_1 = _a.sent();
                        setError(true);
                        onLoadError(id, err_1);
                        return [3 /*break*/, 5];
                    case 4:
                        setLoading(false);
                        return [7 /*endfinally*/];
                    case 5: return [3 /*break*/, 7];
                    case 6:
                        onExpand(id, true, event, allProps);
                        _a.label = 7;
                    case 7: return [2 /*return*/];
                }
            });
        });
    }
    if (loading) {
        return (React.createElement("a", { className: classPrefix + "-tree__switcher" },
            React.createElement(Icon, { type: "loading" })));
    }
    var switcher = React.isValidElement(switcherIcon) ? (switcherIcon) : (React.createElement("div", null, switcherIcon));
    if (error) {
        switcher = React.createElement(Icon, { type: "error" });
    }
    return enable ? (React.createElement("a", { className: classPrefix + "-tree__switcher", onClick: handleExpand }, switcher)) : (React.createElement("div", { className: classPrefix + "-tree__switcher" }));
});
TreeNodeSwitcher.displayName = "TreeNodeSwitcher";
//# sourceMappingURL=TreeNode.js.map